import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlane,
  faChevronUp,
  faChevronDown,
  faUser,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { mockFlights } from "../data/mockFlights";
import { airlines } from "../data/airlines";
import { config } from "../config";
import FlightDetailsModal from "../components/FlightDetailsModal";
import ProgressBar from "../components/ProgressBar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

function BookingDetails() {
  const navigate = useNavigate();
  const [isFareRulesOpen, setIsFareRulesOpen] = useState(false);
  const [isFlightModalOpen, setIsFlightModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    firstName: "",
    middleName: "",
    lastName: "",
    dob: null,
  });
  const [errors, setErrors] = useState({});

  const handleContinue = () => {
    const newErrors = {};
    if (!formData.title) newErrors.title = "Title is required";
    if (!formData.firstName) newErrors.firstName = "First name is required";
    if (!formData.lastName) newErrors.lastName = "Last name is required";
    if (!formData.dob) newErrors.dob = "Date of birth is required";

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      navigate(
        "/booking/summary?st=f18f7517231246cb979cb4ab916d6f4d&flights=32024%3A1%2C32024%3A2"
      );
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const flight = mockFlights[1];
  const airline = airlines[flight.operatorCode];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container  pb-4 mx-auto px-4 md:px-40 lg:px-40">
        <div className="flex justify-between py-6 items-center border-b">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              {flight.origin} <FontAwesomeIcon icon={faPlane} size="sm" />{" "}
              {flight.destination}
            </h1>
            <p className="text-gray-600">
              {flight.outbound_date} •{" "}
              {flight.return_date && `${flight.return_date}`}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold">£{flight.price}.00</p>
            <p className="text-gray-600">Total price</p>
          </div>
        </div>
        <ProgressBar currentStep={1} />

        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12">
                <img
                  src={airline.logo}
                  alt={airline.name}
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Your flights</h2>
                <p className="text-gray-600">
                  {airline.name} • {flight.duration} • {flight.stops}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsFlightModalOpen(true)}
              className="text-gray-500 hover:text-gray-700 p-2"
            >
              <FontAwesomeIcon icon={faChevronRight} size="lg" />
            </button>
          </div>

          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-gray-600">{flight.outbound_date}</p>
              <div className="flex items-center gap-2">
                <p className="font-bold">{flight.origin}</p>
                <p className="text-gray-600">{flight.outbound_time}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-gray-600">{flight.outbound_date}</p>
              <div className="flex items-center gap-2 justify-end">
                <p className="text-gray-600">{flight.outbound_arrival_time}</p>
                <p className="font-bold">{flight.destination}</p>
              </div>
            </div>
          </div>

          {/* Return Flight */}
          {flight.return_date && (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600">{flight.return_date}</p>
                <div className="flex items-center gap-2">
                  <p className="font-bold">{flight.destination}</p>
                  <p className="text-gray-600">{flight.return_time}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-gray-600">{flight.return_date}</p>
                <div className="flex items-center gap-2 justify-end">
                  <p className="text-gray-600">{flight.return_arrival_time}</p>
                  <p className="font-bold">{flight.origin}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Fare Rules */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <button
            className="w-full p-6 flex justify-between items-center"
            onClick={() => setIsFareRulesOpen(!isFareRulesOpen)}
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faPlane}
                  className={`text-${config.primaryColor}`}
                />
              </div>
              <div className="text-left">
                <h2 className="text-lg font-semibold">Fare rules</h2>
                <p className="text-gray-600">Review airline fare rules</p>
              </div>
            </div>
            <FontAwesomeIcon
              icon={isFareRulesOpen ? faChevronUp : faChevronDown}
              className="text-gray-400"
            />
          </button>

          {isFareRulesOpen && (
            <div className="p-6 border-t">
              <div className="mb-6">
                <h3 className="font-semibold mb-2">
                  {flight.origin} → {flight.destination}
                </h3>
                <p className="text-gray-600">
                  Air France does not permit changes before departure from
                  London Heathrow.
                </p>
              </div>

              {flight.return_date && (
                <div>
                  <h3 className="font-semibold mb-2">
                    {flight.destination} → {flight.origin}
                  </h3>
                  <p className="text-gray-600">
                    Air France does not permit changes before departure from
                    Paris Charles De Gaulle.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Passenger Details */}
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faUser}
                  className={`text-${config.primaryColor}`}
                />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Passenger details</h2>
                <p className="text-gray-600">Adult 1</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <p className="text-gray-800">
              Be sure passenger names match travel documents exactly
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <select
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full p-4 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none bg-white"
            >
              <option value="">Title</option>
              <option value="mr">Mr</option>
              <option value="mrs">Mrs</option>
              <option value="ms">Ms</option>
            </select>
            {errors.title && <p className="text-red-500">{errors.title}</p>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                name="firstName"
                placeholder="First name"
                value={formData.firstName}
                onChange={handleInputChange}
                className="w-full p-4 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              {errors.firstName && (
                <p className="text-red-500">{errors.firstName}</p>
              )}
              <input
                type="text"
                name="middleName"
                placeholder="Middle name (optional)"
                value={formData.middleName}
                onChange={handleInputChange}
                className="w-full p-4 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                name="lastName"
                placeholder="Last name"
                value={formData.lastName}
                onChange={handleInputChange}
                className="w-full p-4 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              {errors.lastName && (
                <p className="text-red-500">{errors.lastName}</p>
              )}
              <DatePicker
                selected={formData.dob}
                onChange={(date) => setFormData({ ...formData, dob: date })}
                placeholderText="Date of birth"
                className="w-full p-4 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                showYearDropdown
                dropdownMode="select"
              />
              {errors.dob && <p className="text-red-500">{errors.dob}</p>}
            </div>
          </div>
        </div>

        <button
          onClick={handleContinue}
          className={`w-full bg-${config.primaryColor} text-white py-4 rounded-full hover:bg-purple-700 transition-colors text-lg font-semibold`}
        >
          Continue
        </button>

        {/* Flight Details Modal */}
        <FlightDetailsModal
          flight={flight}
          isOpen={isFlightModalOpen}
          onClose={() => setIsFlightModalOpen(false)}
        />
      </div>
    </div>
  );
}

export default BookingDetails;
